import _ from 'lodash'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { WbsItemSearchConditionVO } from '../../../../domain/value-object/WbsItemSearchConditionVo'
import { WbsItemTypeVO } from '../../../../domain/value-object/WbsItemTypeVO'
import WbsItemApi, {
  createRowByResponse as createWbsItemDataByResponse,
  createWbsItemDeltaInputByRow,
  isWatcherEdited,
  WbsItemBatchDeltaRequest,
  WbsItemDeltaInput,
  WbsItemRow,
  WbsItemSearchDetail,
  WbsItemUpdateBatchDeltaRequest,
} from '../../../../lib/functions/wbsItem'
import { useWbsItemSearchConditionApiRequestTransformService } from '../../../../services/transform-service/wbsItemSearchConditionApiRequestTransformService'
import store from '../../../../store'
import { doNotRequireSave } from '../../../../store/requiredSaveData'
import { getTypeIndex } from '../../../containers/commons/AgGrid/components/cell/custom/wbsItemType'
import { WbsItemSearchRow } from '../wbsItemSearch'
import { ProjectMemberProps } from '../../../../lib/functions/projectMember'
import { isTagColumnEdited } from '../../../../lib/functions/tag'
import { successDummyResponse } from '../../../../lib/commons/api'
import {
  UpdateSprintProductItemProps,
  updateSprintProductItems,
} from '../../../../lib/functions/sprintProductItem'
import SprintBacklogApi, {
  UpdateBatchSprintBacklogProps,
} from '../../../../lib/functions/sprintBacklog'
import { addScreenMessage, MessageLevel } from '../../../../store/messages'
import { intl } from '../../../../i18n'

const FETCH_LIMIT: number = 10000

export interface Props {
  applicationFunctionUuid: string
  projectUuid: string
  searchCondition: WbsItemSearchConditionVO
}

type FetchWbsItemResponase = {
  total: number
  hit: number
  data: WbsItemSearchDetail[]
  offset: number
  limit: number
}

export const useWbsItemSearchData = ({
  applicationFunctionUuid,
  projectUuid,
  searchCondition,
}: Props) => {
  const [totalDataSize, setTotalDataSize] = useState<number>(0)
  const [hitDataSize, setHitDataSize] = useState<number>(0)
  const [count, setCount] = useState<number>(0)
  const [data, setDataInternal] = useState<WbsItemSearchRow[]>([])
  const originalData = useRef<Map<string, WbsItemSearchRow>>(new Map())

  // Fetch ----------
  const getTicketTypeIndex = useCallback((baseTypeDto: WbsItemTypeVO) => {
    let rootType = baseTypeDto
    while (!!rootType.child) {
      rootType = rootType.child
    }
    return getTypeIndex(rootType)
  }, [])

  const createRowByResponse = useCallback(
    (responseData: WbsItemSearchDetail) => {
      const wbsItemRow: WbsItemRow = createWbsItemDataByResponse(
        responseData,
        responseData.cumulation
      )
      wbsItemRow.watchers = wbsItemRow.watchers?.sort(
        (valueA: ProjectMemberProps, valueB: ProjectMemberProps) => {
          return (valueA?.name ?? '').localeCompare(valueB?.name ?? '')
        }
      )

      return {
        uuid: wbsItemRow.uuid,
        lockVersion: wbsItemRow.lockVersion,
        wbsItem: wbsItemRow,
        path: responseData.path,
        cumulation: responseData.cumulation,
        commentSummary: responseData.commentSummary,
        deliverableAttachmentSummary: responseData.deliverableAttachmentSummary,
        typeIndex: getTypeIndex(new WbsItemTypeVO(responseData.baseTypeDto)),
        ticketTypeIndex: getTicketTypeIndex(responseData.baseTypeDto),
        parentWbsItem: {
          ...responseData.parentWbsItem,
          wbsItemType: responseData.parentWbsItem?.typeDto
            ? new WbsItemTypeVO(responseData.parentWbsItem.typeDto)
            : undefined,
        },
        revision: wbsItemRow.revision,
        createdAt: wbsItemRow.createdAt,
        createdBy: wbsItemRow.createdBy,
        updatedAt: wbsItemRow.updatedAt,
        updatedBy: wbsItemRow.updatedBy,
        treeValue: [wbsItemRow.uuid],
      } as WbsItemSearchRow
    },
    [getTicketTypeIndex]
  )

  const searchConditionApiRequestTransformer =
    useWbsItemSearchConditionApiRequestTransformService(projectUuid)

  const searchConditionForApi = useMemo(
    () => searchConditionApiRequestTransformer.toApiRequest(searchCondition),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projectUuid, searchCondition]
  )

  useEffect(() => {
    async function fetchData() {
      const count = await WbsItemApi.countSearchWbsItem(searchConditionForApi)
      const num: number = count.json
      setCount(num)
    }
    fetchData()
  }, [searchConditionForApi])

  const fetch = useCallback(async () => {
    const response = await WbsItemApi.searchWbsItem(searchConditionForApi)
    const fetchData: FetchWbsItemResponase = response.json
    setTotalDataSize(fetchData.total)
    setHitDataSize(fetchData.hit)

    let availableData = fetchData.data
    if (fetchData.hit >= FETCH_LIMIT) {
      // notify user that es can not return over 10,000 hits.
      store.dispatch(
        addScreenMessage(applicationFunctionUuid, {
          type: MessageLevel.WARN,
          title: intl.formatMessage({
            id: 'bulksheet.warning.search.limit',
          }),
          text: intl.formatMessage({
            id: 'bulksheet.warning.search.limit.detail',
          }),
        })
      )
      availableData = fetchData.data.slice(0, FETCH_LIMIT)
    }
    return availableData.map(d => createRowByResponse(d))
  }, [searchConditionForApi, createRowByResponse, applicationFunctionUuid])

  const refresh = useCallback(async () => {
    const rows: WbsItemSearchRow[] | undefined = await fetch()
    if (!rows) return
    setDataInternal(rows)
    originalData.current.clear()
    rows
      .filter(row => !!row.wbsItem)
      .forEach(row => {
        originalData.current.set(row.uuid, _.cloneDeep(row))
      })
    store.dispatch(doNotRequireSave())
  }, [fetch])

  useEffect(() => {
    refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const refreshSingleRow = useCallback(
    async (wbsItemUuid: string | undefined) => {
      if (!wbsItemUuid) return
      const targetData: WbsItemSearchRow | undefined = data.find(
        d => d?.wbsItem?.uuid === wbsItemUuid
      )
      if (!targetData) return
      const response = await WbsItemApi.findByUuid(wbsItemUuid)
      const newRow = createRowByResponse(response.json)
      const newData: WbsItemSearchRow[] = data.map((row: WbsItemSearchRow) => {
        if (row.uuid !== newRow.uuid) return row
        return newRow
      })
      originalData.current.set(newRow.uuid, _.cloneDeep(newRow))
      setDataInternal(newData)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data]
  )

  // Save ----------
  const rowToWbsItemDeltaInput = useCallback(
    (
      row: WbsItemSearchRow | undefined,
      originalDataMap: Map<string, WbsItemSearchRow>
    ) => {
      if (!row) return undefined
      const original: WbsItemSearchRow | undefined = originalDataMap.get(
        row.uuid
      )
      const originalWbsItem: WbsItemRow | undefined = original?.wbsItem
      const wbsItem: WbsItemRow = row.wbsItem

      return createWbsItemDeltaInputByRow(originalWbsItem, wbsItem)
    },
    []
  )

  const updateWbsItem = useCallback(
    async (
      projectUuid: string,
      data: WbsItemSearchRow[],
      originalDataMap: Map<string, WbsItemSearchRow>
    ) => {
      const editedRows = data.filter(
        (row: WbsItemSearchRow) => row.edited && originalDataMap.has(row.uuid)
      )

      const edited = editedRows
        .map((row: WbsItemSearchRow) =>
          rowToWbsItemDeltaInput(row, originalDataMap)
        )
        .filter(v => !!v)

      const watchers = editedRows
        .filter((row: WbsItemSearchRow) => {
          const original =
            originalDataMap.get(row.uuid)?.wbsItem?.watchers ?? []
          const watchers = row.wbsItem?.watchers ?? []
          return isWatcherEdited(original, watchers)
        })
        .map((row: WbsItemSearchRow) => ({
          wbsItemUuid: row.wbsItem?.uuid,
          userUuids: row.wbsItem?.watchers?.map(watcher => watcher.uuid),
        }))

      const tags = editedRows
        .filter((row: WbsItemSearchRow) => {
          const original = originalDataMap.get(row.uuid)?.wbsItem?.tags ?? []
          const watchers = row.wbsItem?.tags ?? []
          return isTagColumnEdited(original, watchers)
        })
        .map((row: WbsItemSearchRow) => ({
          wbsItemUuid: row.wbsItem?.uuid,
          tagUuids: row.wbsItem?.tags?.map(tag => tag.uuid),
        }))

      const response = await WbsItemApi.updateBatchDelta({
        wbsItems: {
          added: [],
          edited,
          deleted: [],
        } as WbsItemUpdateBatchDeltaRequest,
        watchers,
        tags,
      } as WbsItemBatchDeltaRequest)

      return response
    },
    [rowToWbsItemDeltaInput]
  )

  const updateSprintProduct = useCallback(
    async (
      data: WbsItemSearchRow[],
      originalDataMap: Map<string, WbsItemSearchRow>,
      wbsItemLockVersionMap: { [wbsItemUuid: string]: number }
    ) => {
      const deliverables = data.filter(
        (row: WbsItemSearchRow) =>
          row.edited &&
          !!row.wbsItem?.uuid &&
          row.wbsItem?.wbsItemType?.isDeliverable()
      )
      if (_.isEmpty(deliverables)) return successDummyResponse
      const sprintProductRequest: UpdateSprintProductItemProps = {
        added: [],
        edited: [],
        deleted: [],
      }
      deliverables.forEach((row: WbsItemSearchRow) => {
        const wbsItem = row.wbsItem
        if (!wbsItem?.uuid) return
        if (!row.added && !row.edited) return

        const sprint = wbsItem?.sprint
        const originalSprints = originalData.current.get(row.uuid)?.wbsItem
          .sprints
        const deliverableUuid = wbsItem.uuid
        const deliverableLockVersion =
          wbsItemLockVersionMap[wbsItem.uuid] ?? wbsItem.lockVersion!
        if (
          sprint &&
          (!originalSprints ||
            !originalSprints.some(v => v.uuid === sprint.uuid))
        ) {
          sprintProductRequest.added.push({
            deliverableUuid,
            deliverableLockVersion,
            sprintUuid: sprint.uuid,
          })
        }

        // 元々設定されていたスプリントのうち、現在選択されているスプリント以外を削除対象に追加する
        originalSprints?.forEach(v => {
          if (v.uuid === sprint?.uuid) return
          sprintProductRequest.deleted.push({
            deliverableUuid,
            deliverableLockVersion,
            sprintUuid: v.uuid,
          })
        })
      })
      if (
        _.isEmpty(sprintProductRequest.added) &&
        _.isEmpty(sprintProductRequest.deleted)
      ) {
        return successDummyResponse
      }
      const response = await updateSprintProductItems(sprintProductRequest)
      return response
    },
    []
  )

  const updateSprintBacklog = useCallback(
    async (
      data: WbsItemSearchRow[],
      originalDataMap: Map<string, WbsItemSearchRow>,
      wbsItemLockVersionMap: { [wbsItemUuid: string]: number }
    ) => {
      const tasks = data.filter(
        (row: WbsItemSearchRow) =>
          row.edited &&
          !!row.wbsItem?.uuid &&
          row.wbsItem?.wbsItemType?.isTask()
      )
      if (_.isEmpty(tasks)) return successDummyResponse
      const sprintBacklogRequest: UpdateBatchSprintBacklogProps = {
        sprintBacklogItems: [],
        sprintPlans: {
          added: [],
          deleted: [],
        },
      }
      tasks.forEach(row => {
        const wbsItem = row.wbsItem
        const sprint = wbsItem?.sprint
        const originalSprint = originalDataMap.get(row.uuid)?.wbsItem.sprint
        if (
          !!sprint?.uuid &&
          (row.added ||
            (row.edited &&
              (!originalSprint || originalSprint.uuid !== sprint.uuid)))
        ) {
          sprintBacklogRequest.sprintPlans.added.push({
            taskUuid: wbsItem.uuid!,
            taskLockVersion:
              wbsItemLockVersionMap[wbsItem.uuid!] ?? wbsItem.lockVersion!,
            sprintUuid: sprint.uuid,
          })
        } else if (row.edited && !sprint?.uuid && !!originalSprint) {
          sprintBacklogRequest.sprintPlans.deleted.push({
            taskUuid: wbsItem.uuid!,
            taskLockVersion:
              wbsItemLockVersionMap[wbsItem.uuid!] ?? wbsItem.lockVersion!,
            sprintUuid: originalSprint.uuid,
          })
        }
      })
      if (
        _.isEmpty(sprintBacklogRequest.sprintPlans.added) &&
        _.isEmpty(sprintBacklogRequest.sprintPlans.deleted)
      ) {
        return successDummyResponse
      }
      return SprintBacklogApi.updateBatchSprintBacklog(sprintBacklogRequest)
    },
    []
  )

  const saveWbsItem = useCallback(
    async (saveData: WbsItemSearchRow[]) => {
      const updateWbsItemResponse = await updateWbsItem(
        projectUuid,
        saveData,
        originalData.current
      )
      const wbsItemLockVersionMap = updateWbsItemResponse?.json.edited
        ? Object.fromEntries(
            [...updateWbsItemResponse?.json.edited].map(v => [
              v.uuid,
              v.lockVersion,
            ])
          )
        : {}
      const updateSprintProductResponse = await updateSprintProduct(
        saveData,
        originalData.current,
        wbsItemLockVersionMap
      )
      const updateSprintBacklogResponse = await updateSprintBacklog(
        saveData,
        originalData.current,
        wbsItemLockVersionMap
      )
      return [
        updateWbsItemResponse,
        updateSprintProductResponse,
        updateSprintBacklogResponse,
      ]
    },
    [projectUuid, updateWbsItem, updateSprintProduct, updateSprintBacklog]
  )

  const save = useCallback(async () => {
    const response = await saveWbsItem(data)
    return response
  }, [data, saveWbsItem])

  const saveSingleRow = useCallback(
    async (uuid: string) => {
      const target = data.filter(row => row.uuid === uuid && row.edited)
      if (!target) return
      await saveWbsItem(target)
    },
    [data, saveWbsItem]
  )

  const generateWbsItemDeltaRequest = useCallback(
    (row: WbsItemSearchRow | undefined): WbsItemDeltaInput | undefined =>
      rowToWbsItemDeltaInput(row, originalData.current),
    [originalData, rowToWbsItemDeltaInput]
  )

  return {
    totalDataSize,
    hitDataSize,
    count,
    data,
    refresh,
    refreshSingleRow,
    save,
    saveSingleRow,
    generateWbsItemDeltaRequest,
  }
}
